<template>
  <div class="overlay">
    <div class="scoreboard" @click.stop>
      <h3 v-if="final">Winter has ended</h3>
      <h3 v-else>{{ seasonName }} scoring</h3>

      <table class="score-table">
        <thead>
          <tr>
            <th class="score-table__name" />
            <th v-for="part in headers" :key="part.label" :title="part.title">{{ part.label }}</th>
            <th>★</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.seat"
            :class="{
              'score-table__me': row.seat === mySeat,
              'score-table__winner': final && row.winner && revealDone,
            }"
          >
            <td class="score-table__name">{{ row.name }}</td>
            <td v-for="(v, i) in row.parts" :key="i">
              <span v-if="i < revealStep" class="score-reveal">{{ v }}</span>
            </td>
            <td class="score-table__total">
              <span v-if="revealStep > partCount" class="score-reveal">{{ shownTotal(row) }}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <p v-if="final && winnerLine && revealDone" class="scoreboard__winner score-reveal">
        {{ winnerLine }}
      </p>

      <button class="primary" @click="onButton">
        <template v-if="!revealDone">Show me</template>
        <template v-else-if="final">Back to the menu</template>
        <template v-else>On to {{ nextSeasonName }}</template>
      </button>
    </div>
  </div>
</template>

<script>
// Season-end interstitial and the final reckoning, one component: the same
// table, columns per scored edict plus coins and monsters, per the score
// sheet's own layout. Final mode sums the four seasons and applies the
// rulebook's tie-break (p9): fewest stars lost to monsters, then shared.
//
// The reveal is a MOMENT, not a flash (bug report 2026-08-29: "the first
// round of scoring went by and I didn't even notice"): the columns land one
// at a time, then the totals count up, and the button won't advance the
// game until the reveal has finished - the first tap merely completes it.
import { mapGetters } from 'vuex';
import { scoringCardByKey, SEASONS } from '@/engine/cards';
import { finalStandings } from '@/engine/standings';

export const REVEAL_STEP_MS = 600;
export const COUNT_TICK_MS = 45;

export default {
  name: 'ScoreBoard',
  props: {
    view: { type: Object, required: true },
    players: { type: Array, required: true },
    seasonIndex: { type: Number, default: null },
    final: { type: Boolean, default: false },
  },
  emits: ['close'],
  data() {
    return {
      revealStep: 0, // how many part-columns are showing
      totalProgress: 0, // 0..1, the totals counting up
    };
  },
  computed: {
    ...mapGetters(['mySeat']),
    seasonName() { return SEASONS[this.seasonIndex]?.name || ''; },
    nextSeasonName() { return SEASONS[this.seasonIndex + 1]?.name || 'the end'; },
    scores() { return this.view.seasonScores[this.seasonIndex] || []; },
    partCount() { return this.headers.length; },
    revealDone() { return this.revealStep > this.partCount && this.totalProgress >= 1; },
    headers() {
      if (this.final) {
        return SEASONS.map((s) => ({ label: s.name.slice(0, 2), title: s.name }));
      }
      const parts = (this.scores[0]?.edicts || []).map((e) => ({
        label: e.letter,
        title: scoringCardByKey(e.key).name,
      }));
      return [...parts, { label: '🪙', title: 'Coins' }, { label: '👹', title: 'Monsters' }];
    },
    rows() {
      // Final: the engine owns the p9 tie-break, so this table and the
      // lifetime win counter can't come to different conclusions about the
      // same game (engine/standings.js).
      if (this.final) {
        const nameOf = (seat) => this.players.find((p) => p.seat === seat)?.name || '';
        return finalStandings(this.view).map((row) => ({
          seat: row.seat,
          name: nameOf(row.seat),
          parts: row.perSeason,
          total: row.total,
          monsters: row.monsters,
          winner: row.winner,
        }));
      }
      return this.players.map((p) => {
        const s = this.scores[p.seat];
        return {
          seat: p.seat,
          name: p.name,
          parts: [...s.edicts.map((e) => e.stars), s.coins, s.monsters ? `-${s.monsters}` : 0],
          total: s.total,
        };
      });
    },
    winnerLine() {
      const winners = this.rows.filter((r) => r.winner);
      if (!winners.length) return '';
      if (winners.length > 1) return `${winners.map((w) => w.name).join(' and ')} share the victory.`;
      const w = winners[0];
      return w.seat === this.mySeat
        ? 'You are the greatest cartographer in the kingdom!'
        : `${w.name} is the greatest cartographer in the kingdom.`;
    },
  },
  mounted() {
    // jsdom has no matchMedia; treat that as "animate".
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      this.finishReveal();
      return;
    }
    this.stepTimer = window.setInterval(() => this.advanceReveal(), REVEAL_STEP_MS);
  },
  beforeUnmount() {
    this.clearTimers();
  },
  methods: {
    clearTimers() {
      if (this.stepTimer) { window.clearInterval(this.stepTimer); this.stepTimer = null; }
      if (this.countTimer) { window.clearInterval(this.countTimer); this.countTimer = null; }
    },
    advanceReveal() {
      this.revealStep += 1;
      if (this.revealStep > this.partCount) {
        window.clearInterval(this.stepTimer);
        this.stepTimer = null;
        this.countTimer = window.setInterval(() => {
          this.totalProgress = Math.min(1, this.totalProgress + 0.08);
          if (this.totalProgress >= 1) {
            window.clearInterval(this.countTimer);
            this.countTimer = null;
          }
        }, COUNT_TICK_MS);
      }
    },
    finishReveal() {
      this.clearTimers();
      this.revealStep = this.partCount + 1;
      this.totalProgress = 1;
    },
    shownTotal(row) {
      return Math.round(row.total * this.totalProgress);
    },
    // The stop is real: while the reveal is running the button only hurries
    // it along; the game does not advance until a tap lands on the finished
    // table.
    onButton() {
      if (!this.revealDone) { this.finishReveal(); return; }
      this.$emit('close');
    },
  },
};
</script>
